import React, { useState, useMemo, useCallback } from 'react';
import { Box } from '@mui/material';
import _, { Dictionary } from 'lodash';
import {
  ClientNamesStatus,
  DialogActions,
  DialogLabel,
  DialogTitle,
  EmptyValueBox,
  ListSubheader,
  MenuItemRow,
  ReassignSpaceTypeDialogContent,
  ReassignSpaceTypesDialogContainer,
  SpaceCategoryColorDot,
  SpaceCategorySelect,
  SpaceTypeSelect,
  SpaceTypesSelectContainer,
  ValueRow,
} from 'spw-components/dialogs/spaces/styles';
import { IAppState } from 'spw-redux/reducer';
import { connect, useDispatch, useSelector } from 'react-redux';
import { IProjectSpaceTypeCategory } from 'spw-redux/modules/projects/types';
import { IProjectSpaceType } from 'spw-redux/modules/space_types/types';
import { isNil } from 'ramda';
import {
  SPWCancelButton,
  SPWSaveButton,
} from 'spw-shared/globalComponents/buttons';
import {
  reSortSpaceTypesAfterDelete,
  sortRowsByPreviousId,
} from 'spw-redux/modules/calculators/utils';
import {
  bulkUpdateProjectSpaceTypes,
  reassignProjectSpaceType,
} from 'spw-redux/modules/space_types/actions';
import { ColorDot } from 'spw-components/worksheet/styles';
import {
  DEFAULT_INDIVIDUAL_NAME,
  isStorageSupportSpace,
} from 'spw-components/worksheet/utils';
import { colors } from 'spw-theme';
import { IWorksheetRow } from 'spw-redux/modules/calculators/types';
import { Row } from 'spw-shared/globalComponents';
import { getColor, removeLineBreaks } from 'spw-shared/utils';
import {
  currentProject,
  projectColors as selectProjectColors,
  projectSpaceTypes as projectSpaceTypesSelector,
} from 'spw-redux/modules/projects/selectors';
import { EXISTING_SITE_ANALYSIS, PROGRAMMING } from 'spw-constants';
import { isIWCategoryOrSubcategory } from 'spw-projects/utils';

interface ReassignSpaceTypesProps {
  showDialog: boolean;
  handleOnClose: () => void;
  clientFacingNamesAreVisible?: boolean;
}

const ReassignSpaceTypesDialog: React.FC<ReassignSpaceTypesProps> = ({
  showDialog,
  handleOnClose,
  clientFacingNamesAreVisible,
}) => {
  const project = useSelector(currentProject);
  const projectSpaceTypes = useSelector(projectSpaceTypesSelector);
  const dispatch = useDispatch();
  const [selectedSpaceType, setSelectedSpaceType] = useState(0);
  const [category, setCategory] = useState<IProjectSpaceTypeCategory>(null);
  const [newCategory, setNewCategory] = useState<IProjectSpaceTypeCategory>(
    null
  );
  const projectColors = useSelector(selectProjectColors);

  const spaceTypeCategories = useMemo(() => {
    return project.spaceTypeCategories.filter(
      (projectSpaceTypeCategory: IProjectSpaceTypeCategory) =>
        !isIWCategoryOrSubcategory(projectSpaceTypeCategory)
    );
  }, [project.spaceTypeCategories]);

  const refMap: Record<
    number,
    HTMLDivElement | null
  > = spaceTypeCategories.reduce(
    (map, spaceTypeCategory: IProjectSpaceTypeCategory) => {
      return {
        ...map,
        [spaceTypeCategory.id]: null,
      };
    },
    {}
  );

  const spaceTypesGroupedByCategory: Dictionary<
    Partial<IProjectSpaceType>[]
  > = useMemo(() => {
    return (
      _(projectSpaceTypes)
        .map(
          (projectSpaceType) =>
            ({
              projectSpaceType,
              category: projectSpaceType.category,
              spaceType: projectSpaceType.spaceType,
            } as IWorksheetRow)
        )
        // group by category for sorting
        .groupBy(
          (projectSpaceType: unknown) =>
            (projectSpaceType as IProjectSpaceType)?.category?.id
        )
        .values()
        .map(sortRowsByPreviousId)
        .flatten()
        // map items back to raw projectSpaceTypes
        .map((row) => row.projectSpaceType)
        // filter out un-changeable items
        .filter((projectSpaceType) => {
          if (project.type === PROGRAMMING) {
            return (
              !isIWCategoryOrSubcategory(projectSpaceType.category) &&
              !isStorageSupportSpace(projectSpaceType.spaceType)
            );
          }
          // Storage and support spaces are not filtered out as they're part of the
          // linked list in ESA projects
          return !isIWCategoryOrSubcategory(projectSpaceType.category);
        })
        // regroup by category for display
        .groupBy(
          (projectSpaceType: unknown) =>
            (projectSpaceType as IProjectSpaceType)?.category?.id
        )
        .value() as Dictionary<Partial<IProjectSpaceType>[]>
    );
  }, [projectSpaceTypes, project.type]);

  const selectedNewCategory = useMemo(() => {
    return spaceTypeCategories.find(
      (spaceTypeCategory: IProjectSpaceTypeCategory) =>
        spaceTypeCategory.id === newCategory?.id
    );
  }, [spaceTypeCategories, newCategory]);

  const reassignSpaceType = useCallback(() => {
    const currentProjectSpaceType = spaceTypesGroupedByCategory[
      category.id
    ].find(
      (projectSpaceType: IProjectSpaceType) =>
        projectSpaceType.spaceType.id === selectedSpaceType
    );

    if (project.type === PROGRAMMING) {
      const { projectSpaceTypesToUpdate } = reSortSpaceTypesAfterDelete(
        spaceTypesGroupedByCategory[category.id] as IProjectSpaceType[],
        currentProjectSpaceType as IProjectSpaceType
      );

      const categorySpaceTypes =
        spaceTypesGroupedByCategory[selectedNewCategory?.id];

      const lastOfNewCat = categorySpaceTypes[categorySpaceTypes.length - 1];
      const newPreviousId = lastOfNewCat.id;

      const updateProjectSpaceType = {
        category: newCategory,
        id: currentProjectSpaceType.id,
        spaceTypeId: currentProjectSpaceType.spaceType.id,
        previousId: newPreviousId,
      };

      projectSpaceTypesToUpdate.push(
        (updateProjectSpaceType as unknown) as IProjectSpaceType
      );

      dispatch(bulkUpdateProjectSpaceTypes(projectSpaceTypesToUpdate));
    } else if (project.type === EXISTING_SITE_ANALYSIS) {
      dispatch(
        reassignProjectSpaceType(currentProjectSpaceType.id, newCategory?.id)
      );
    }

    handleOnClose();
  }, [
    category?.id,
    project.type,
    selectedNewCategory?.id,
    newCategory,
    selectedSpaceType,
    handleOnClose,
    spaceTypesGroupedByCategory,
    dispatch,
  ]);

  const isDisabled =
    !category || !newCategory || category.id === selectedNewCategory.id;

  const className = 'add-margin-right';
  return (
    <ReassignSpaceTypesDialogContainer
      open={showDialog}
      onClose={handleOnClose}
    >
      <Row justifyContent="space-between" alignItems="center">
        <DialogTitle>Reassign a Space Type</DialogTitle>
        <Box marginRight={3}>
          <ClientNamesStatus
            clientFacingNamesAreVisible={clientFacingNamesAreVisible}
          />
        </Box>
      </Row>
      <ReassignSpaceTypeDialogContent>
        <p>
          This is a global change that will update on all{' '}
          {project.type === PROGRAMMING ? 'program worksheets' : 'floor plans'}{' '}
          within this project.
        </p>
        <p>
          If data is entered on a{' '}
          {project.type === PROGRAMMING ? 'worksheet' : 'floor plan'} for the
          space you are reassigning, that data will move over along with the
          space.
        </p>

        <SpaceTypesSelectContainer>
          <Box display="flex" alignItems="center">
            <DialogLabel>Reassign</DialogLabel>
          </Box>
          <Box>
            <SpaceTypeSelect
              disableUnderline
              displayEmpty
              value={selectedSpaceType || ''}
              renderValue={(value: any) => {
                if (!value) {
                  return <EmptyValueBox>Select a space type</EmptyValueBox>;
                }

                const currentSpaceType = spaceTypesGroupedByCategory[
                  category.id
                ].find(
                  (projectSpaceType: IProjectSpaceType) =>
                    projectSpaceType.spaceType.id === value
                );

                return (
                  <ValueRow>
                    <ColorDot
                      color={getColor(
                        category.defaultSpaceTypeCategory.category,
                        projectColors
                      )}
                      className={className}
                    />{' '}
                    {clientFacingNamesAreVisible &&
                    currentSpaceType.clientFacingName
                      ? currentSpaceType.clientFacingName
                      : currentSpaceType.spaceType.name}
                  </ValueRow>
                );
              }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (isNil(e.target.value)) {
                  return;
                }
                setSelectedSpaceType(parseInt(e.target.value, 10));
              }}
            >
              <Box
                display="flex"
                style={{
                  paddingBottom: '8px',
                  paddingLeft: '16px',
                  position: 'sticky',
                  top: '0',
                  paddingTop: '8px',
                  borderBottom: '1px solid #e0e0e0',
                  zIndex: 10,
                  background: colors.white,
                }}
              >
                {spaceTypeCategories.map(
                  (spaceTypeCategory: IProjectSpaceTypeCategory) => {
                    return (
                      <SpaceCategoryColorDot
                        color={getColor(
                          spaceTypeCategory.defaultSpaceTypeCategory.category,
                          projectColors
                        )}
                        key={`color-dot-${spaceTypeCategory.id}`}
                        className={className}
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          if (refMap[spaceTypeCategory.id]) {
                            refMap[spaceTypeCategory.id].scrollIntoView({
                              behavior: 'smooth',
                              block: 'start',
                            });
                          }
                        }}
                      >
                        <img
                          src={`/images/${spaceTypeCategory.icon}.svg`}
                          alt="Space Icon Dots"
                        />
                      </SpaceCategoryColorDot>
                    );
                  }
                )}
              </Box>

              {spaceTypeCategories.reduce(
                (categoriesList, spaceTypeCategory) => {
                  return categoriesList.concat([
                    <div
                      key={`${spaceTypeCategory.id}-ref`}
                      ref={(node) => {
                        refMap[spaceTypeCategory.id] = node;
                      }}
                      style={{ scrollMarginTop: '48px' }}
                    />,
                    <ListSubheader
                      key={`sub-header-${spaceTypeCategory.id}`}
                      disableSticky
                    >
                      {spaceTypeCategory.name}
                    </ListSubheader>,
                    spaceTypesGroupedByCategory[spaceTypeCategory.id]
                      ?.filter((projectSpaceType: IProjectSpaceType) => {
                        const canSpaceTypeBeReassigned =
                          projectSpaceType.spaceType.name !==
                          DEFAULT_INDIVIDUAL_NAME;
                        if (project.type === EXISTING_SITE_ANALYSIS) {
                          return (
                            canSpaceTypeBeReassigned &&
                            !isStorageSupportSpace(projectSpaceType.spaceType)
                          );
                        }
                        return canSpaceTypeBeReassigned;
                      })
                      .map((projectSpaceType: IProjectSpaceType) => {
                        return (
                          <MenuItemRow
                            key={`menu-item-${projectSpaceType.spaceType.id}`}
                            value={projectSpaceType.spaceType.id}
                            onClick={() => {
                              setCategory(spaceTypeCategory);
                            }}
                          >
                            <ColorDot
                              color={getColor(
                                spaceTypeCategory.defaultSpaceTypeCategory
                                  .category,
                                projectColors
                              )}
                              className={className}
                            />
                            <pre
                              style={{
                                maxWidth: 'calc(100% - 32px)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {removeLineBreaks(
                                clientFacingNamesAreVisible &&
                                  projectSpaceType.clientFacingName
                                  ? projectSpaceType.clientFacingName
                                  : projectSpaceType.spaceType.name
                              )}
                            </pre>
                          </MenuItemRow>
                        );
                      }),
                  ]);
                },
                []
              )}
            </SpaceTypeSelect>
          </Box>
          <Box display="flex" alignItems="center">
            <DialogLabel className="ml-16">to</DialogLabel>
          </Box>
          <Box>
            <SpaceCategorySelect
              disabled={!category}
              disableUnderline
              displayEmpty
              value={newCategory?.id || ''}
              renderValue={(value: any) => {
                if (!value) {
                  return <EmptyValueBox>Select a space category</EmptyValueBox>;
                }
                return (
                  <ValueRow>
                    <SpaceCategoryColorDot
                      color={getColor(
                        selectedNewCategory.defaultSpaceTypeCategory.category,
                        projectColors
                      )}
                      className={className}
                    >
                      <img
                        src={`/images/${selectedNewCategory.icon}.svg`}
                        alt={category.name}
                      />
                    </SpaceCategoryColorDot>
                    {selectedNewCategory?.name}
                  </ValueRow>
                );
              }}
            >
              {spaceTypeCategories.map(
                (
                  spaceTypeCategory: IProjectSpaceTypeCategory,
                  index: number
                ) => {
                  return (
                    <MenuItemRow
                      key={spaceTypeCategory.id}
                      onClick={() => {
                        setNewCategory(spaceTypeCategory);
                      }}
                      value={spaceTypeCategory.id}
                      disabled={category?.id === spaceTypeCategory?.id}
                      className={index === 0 ? 'first' : ''}
                      style={{ height: '40px' }}
                    >
                      <SpaceCategoryColorDot
                        color={getColor(
                          spaceTypeCategory.defaultSpaceTypeCategory.category,
                          projectColors
                        )}
                        className={className}
                      >
                        <img
                          src={`/images/${spaceTypeCategory.icon}.svg`}
                          alt={spaceTypeCategory.name}
                        />
                      </SpaceCategoryColorDot>
                      {spaceTypeCategory.name}
                    </MenuItemRow>
                  );
                }
              )}
            </SpaceCategorySelect>
          </Box>
        </SpaceTypesSelectContainer>

        <DialogActions>
          <SPWSaveButton disabled={isDisabled} onClick={reassignSpaceType} />
          <SPWCancelButton onClick={handleOnClose} />
        </DialogActions>
      </ReassignSpaceTypeDialogContent>
    </ReassignSpaceTypesDialogContainer>
  );
};

const mapStateToProps = (app: IAppState) => ({
  project: app.projectsReducer.currentProject,
});

export default connect(mapStateToProps)(ReassignSpaceTypesDialog);
